import { api } from "./api";

/**
 * The audit trail for admin-managed entities — prompts and API configurations.
 *
 * Distinct from `auditLogApi`, which serves the project-scoped trail. That one is
 * recorded from the client after each audited action; these entries are written
 * server-side inside the transaction that makes the change, so there is nothing to
 * record from here — this module only reads.
 *
 * The two entities live in different services with different route prefixes, but
 * their tables are identical, so one entry type and one dialog cover both.
 */

export type AdminAuditChangeType = "created" | "updated" | "deleted";

/** Old and new value of one field, as computed server-side. */
export interface AdminAuditFieldChange {
  old: unknown;
  new: unknown;
}

export interface AdminAuditEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  change_type: AdminAuditChangeType | string;
  changed_by_sub: string | null;
  changed_by_email: string | null;
  changed_at: string;
  /** Only the fields that changed. Empty for a save that altered nothing. */
  diff: Record<string, AdminAuditFieldChange>;
}

export interface AdminAuditPage {
  items: AdminAuditEntry[];
  /** Entries for this entity ignoring limit/offset — what the pager counts. */
  total: number;
}

export interface AdminAuditPageParams {
  limit: number;
  offset: number;
}

/** Which entity's history is being read; picks the service the request goes to. */
export type AdminAuditSource = "prompt" | "api_configuration";

const HISTORY_PATHS: Record<AdminAuditSource, (entityId: string) => string> = {
  // Proxied to heor.service.prompt-library through the gateway's /api/prompts namespace.
  prompt: (entityId) => `/api/prompts/prompt-libraries/${entityId}/audit-log`,
  api_configuration: (entityId) => `/api/admin/api-configurations/${entityId}/audit-log`,
};

export const adminAuditApi = {
  getHistory: (
    source: AdminAuditSource,
    entityId: string,
    { limit, offset }: AdminAuditPageParams
  ): Promise<AdminAuditPage> => {
    const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return api.get(`${HISTORY_PATHS[source](entityId)}?${query.toString()}`);
  },
};
